// @ts-check
// Páginas de WhatsApp do menu Campanhas, montadas por client/src/app/app.js (spec-2.0.0 seção 4.10; contrato 09 de
// WhatsApp). As páginas do modelo são da frente 1D; a lista e a campanha (editor em rascunho e agendada, relatório a
// partir da preparação), da 2B.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { navigate } from '../../app/router.js';
import { getWhatsappCampaign } from '../../data/whatsapp-campaigns.js';
import { ApiError } from '../../data/api-client.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Button } from '../../ui/button.js';
import { EmptyState } from '../../ui/feedback.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { opensEditor } from '../../lib/whatsapp-campaigns.js';
import { WhatsappTemplateFormPage } from '../campaign-templates/whatsapp-template-form-page.js';
import { WhatsappTemplateDetailPage } from '../campaign-templates/whatsapp-template-detail-page.js';
import { WhatsappCampaignsPage } from './whatsapp-campaigns-page.js';
import { WhatsappCampaignEditorPage, WhatsappCampaignEditorSkeleton } from './whatsapp-campaign-editor-page.js';
import { WhatsappCampaignReport } from './whatsapp-campaign-report.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/whatsapp-campaigns.js').WhatsappCampaign} WhatsappCampaign */

/** Lista das campanhas e comunicados de WhatsApp (/campaigns/whatsapp). */
export function WhatsappCampaignListPage() {
  return html`<${WhatsappCampaignsPage} />`;
}

/** Campanha de WhatsApp: editor e relatório (/campaigns/whatsapp/:campaignId). @param {{ campaignId: string }} props */
export function WhatsappCampaignDetailPage({ campaignId }) {
  const [campaign, setCampaign] = useState(/** @type {WhatsappCampaign | null} */ (null));
  const [loadState, setLoadState] = useState(/** @type {'loading' | 'ready' | 'notFound' | 'error'} */ ('loading'));
  const load = () => {
    setLoadState('loading');
    getWhatsappCampaign(campaignId)
      .then((value) => {
        setCampaign(value);
        setLoadState('ready');
      })
      .catch((error) => {
        if (error instanceof ApiError && error.statusCode === 404) setLoadState('notFound');
        else {
          showErrorSnackbar(error);
          setLoadState('error');
        }
      });
  };
  useEffect(load, [campaignId]);

  if (loadState === 'ready' && campaign) {
    return opensEditor(campaign.status)
      ? html`<${WhatsappCampaignEditorPage} key=${`${campaign.id}-${campaign.status}`} campaignId=${campaign.id} initialCampaign=${campaign} onChanged=${setCampaign} />`
      : html`<${WhatsappCampaignReport} campaign=${campaign} onChanged=${setCampaign} />`;
  }
  return html`<${PageCard} className="campaigns-screen wa-campaign-editor-page" header=${html`<${PageHeader} icon="IconBrandWhatsapp" title=${t('whatsappCampaigns.title')} />`}>
    ${loadState === 'loading'
      ? html`<${WhatsappCampaignEditorSkeleton} />`
      : loadState === 'notFound'
        ? html`<${EmptyState} icon="IconBrandWhatsapp" title=${t('whatsappCampaigns.notFound.title')} subtitle=${t('whatsappCampaigns.notFound.description')}
            action=${html`<${Button} title=${t('whatsappCampaigns.notFound.back')} variant="secondary" size="small" onClick=${() => navigate('/campaigns/whatsapp')} />`} />`
        : html`<div class="wa-campaign-state" role="alert"><p>${t('whatsappCampaigns.list.loadFailed')}</p>
            <${Button} title=${t('whatsappCampaigns.list.retry')} icon="IconRefresh" variant="secondary" size="small" onClick=${load} /></div>`}
  <//>`;
}

/**
 * Modelo de WhatsApp de campanha, novo ou existente (/campaigns/templates/whatsapp/new e /:templateId), da frente 1D:
 * o formulário de criação e a página do modelo (client/src/modules/campaign-templates/).
 * @param {{ templateId: string | null }} props
 */
export function WhatsappTemplateEditorPage({ templateId }) {
  return templateId ? html`<${WhatsappTemplateDetailPage} templateId=${templateId} />` : html`<${WhatsappTemplateFormPage} />`;
}
